import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, CircularProgress, IconButton, Paper, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Autocomplete, Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getCategoriasFlat, getFasesDeObra, addMultipleItens } from '../services/catalogoService';
import { getHierarchicalCategories } from '../utils/categoryUtils';
import { findBestMatch } from '../utils/stringSimilarity';
import { supabase } from '../services/supabaseClient';

interface ImportDocumentModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

interface ExtractedItem {
    id_temp: string;
    descricao: string;
    categoria_nome: string;
    fases_nomes: string[];
    valor_unitario_padrao: number;
    similarExists?: boolean;
    similarItemName?: string;
}

const ImportDocumentModal: React.FC<ImportDocumentModalProps> = ({ open, onClose, onSuccess }) => {
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [items, setItems] = useState<ExtractedItem[]>([]);

    const [categorias, setCategorias] = useState<any[]>([]);
    const [fases, setFases] = useState<any[]>([]);
    const [existingItemNames, setExistingItemNames] = useState<string[]>([]);

    // Batch actions state in Step 2
    const [batchCategoriaNome, setBatchCategoriaNome] = useState<string>('');
    const [batchFasesNomes, setBatchFasesNomes] = useState<string[]>([]);

    useEffect(() => {
        if (open) {
            setStep(1);
            setFile(null);
            setItems([]);
            setError(null);
            setBatchCategoriaNome('');
            setBatchFasesNomes([]);
            loadMetadata();
        }
    }, [open]);

    const loadMetadata = async () => {
        try {
            const [cats, fss, { data: itens }] = await Promise.all([
                getCategoriasFlat(),
                getFasesDeObra(),
                supabase.from('catalogo_itens').select('descricao').eq('ativo', true)
            ]);
            setCategorias(cats || []);
            setFases(fss || []);
            setExistingItemNames((itens || []).map(i => i.descricao));
        } catch (err) {
            console.error("Erro ao carregar metadados", err);
        }
    };

    const hierarchicalCategorias = useMemo(() => getHierarchicalCategories(categorias), [categorias]);
    const leafCategorias = useMemo(() => hierarchicalCategorias.filter(c => !c.isParent), [hierarchicalCategorias]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const extractTextFromPDF = async (inputFile: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(inputFile);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = err => reject(err);
        });
    };

    const processDocument = async () => {
        if (!file) return;
        setLoading(true);
        setError(null);

        try {
            const fileBase64Full = await extractTextFromPDF(file);
            const base64Data = fileBase64Full.split(',')[1];
            const mimeType = file.type || 'application/pdf';

            // Only pass selectable leaf subcategories to Gemini so it never picks a parent category
            const catNames = leafCategorias.map(c => c.nome);
            const faseNames = fases.map(f => f.nome);

            const { data, error: functionError } = await supabase.functions.invoke('extract-items', {
                body: {
                    content: base64Data,
                    mimeType: mimeType,
                    categories: catNames,
                    fases: faseNames
                }
            });

            if (functionError) throw functionError;
            if (data.error) throw new Error(data.error);

            const extracted = (data.items || []).map((item: any, index: number) => {
                const match = findBestMatch(item.descricao || '', existingItemNames, 0.75);
                const validCat = leafCategorias.find(c => c.nome.toLowerCase() === String(item.categoria_nome || '').toLowerCase());
                return {
                    ...item,
                    categoria_nome: validCat ? validCat.nome : '',
                    fases_nomes: Array.isArray(item.fases_nomes)
                        ? item.fases_nomes.filter((fn: string) => fases.some(f => f.nome === fn))
                        : [],
                    id_temp: `temp_${index}`,
                    similarExists: match.score >= 0.75,
                    similarItemName: match.bestMatch || undefined
                };
            });

            setItems(extracted);
            setStep(2);
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Falha ao processar o documento. Verifique a chave da API.');
        } finally {
            setLoading(false);
        }
    };

    const handleItemChange = (id: string, field: keyof ExtractedItem, value: any) => {
        setItems(prev => prev.map(item => item.id_temp === id ? { ...item, [field]: value } : item));
    };

    const handleRemoveItem = (id: string) => {
        setItems(prev => prev.filter(item => item.id_temp !== id));
    };

    const handleApplyBatchCategoria = () => {
        if (!batchCategoriaNome) return;
        setItems(prev => prev.map(item => ({ ...item, categoria_nome: batchCategoriaNome })));
    };

    const handleApplyBatchFases = () => {
        if (batchFasesNomes.length === 0) return;
        setItems(prev => prev.map(item => ({ ...item, fases_nomes: batchFasesNomes })));
    };

    const handleConfirm = async () => {
        setError(null);

        const invalidItems = items.filter(item => {
            const cat = leafCategorias.find(c => c.nome === item.categoria_nome);
            return !item.descricao?.trim() || !cat || !item.fases_nomes || item.fases_nomes.length === 0;
        });

        if (invalidItems.length > 0) {
            setError(`Existem ${invalidItems.length} item(ns) sem Subcategoria ou sem Fase da Obra selecionada. Preencha todos antes de confirmar.`);
            return;
        }

        setLoading(true);
        try {
            const payload = items.map(item => {
                const cat = leafCategorias.find(c => c.nome === item.categoria_nome);
                const itemFasesIds = item.fases_nomes
                    .map(nome => fases.find(f => f.nome === nome)?.id)
                    .filter(Boolean) as number[];

                return {
                    item: {
                        descricao: item.descricao.trim(),
                        categoria_id: cat ? cat.id : null,
                        valor_unitario_padrao: Number(item.valor_unitario_padrao) || 0,
                        ativo: true
                    },
                    fases: itemFasesIds
                };
            });

            await addMultipleItens(payload);
            setStep(3);
        } catch (err: any) {
            setError(err.message || 'Falha ao salvar os itens no banco de dados.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                Importar Itens
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

                {step === 1 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                        <CloudUploadIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>Selecione um arquivo (PDF, PNG ou JPG)</Typography>
                        <input
                            accept=".pdf,image/*"
                            style={{ display: 'none' }}
                            id="raised-button-file"
                            type="file"
                            onChange={handleFileChange}
                        />
                        <label htmlFor="raised-button-file">
                            <Button variant="outlined" component="span" sx={{ mt: 2 }}>
                                Escolher Arquivo
                            </Button>
                        </label>
                        {file && <Typography variant="body2" sx={{ mt: 2 }}>Arquivo selecionado: {file.name}</Typography>}
                    </Box>
                )}

                {step === 2 && (
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Revise os itens extraídos. Itens destacados em amarelo já parecem existir no catálogo (se mantidos com o mesmo nome, terão seus dados atualizados).
                        </Typography>

                        {/* Ações em Lote */}
                        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography variant="body2" fontWeight="bold">Aplicar em lote:</Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '1 1 260px' }}>
                                <Autocomplete
                                    size="small"
                                    fullWidth
                                    options={hierarchicalCategorias}
                                    getOptionLabel={(option) => option.parentName ? `${option.parentName} > ${option.nome}` : option.nome}
                                    value={hierarchicalCategorias.find(c => c.nome === batchCategoriaNome) || null}
                                    onChange={(_, newValue) => setBatchCategoriaNome(newValue ? newValue.nome : '')}
                                    getOptionDisabled={(option) => option.isParent}
                                    renderInput={(params) => <TextField {...params} label="Subcategoria para todos" />}
                                />
                                <Button size="small" variant="outlined" onClick={handleApplyBatchCategoria} disabled={!batchCategoriaNome}>
                                    Aplicar
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '1 1 320px' }}>
                                <Autocomplete
                                    multiple
                                    size="small"
                                    fullWidth
                                    options={fases}
                                    groupBy={(option) => option.tipo_fluxo === 'leilao' ? '🏠 Leilão' : '🏗️ Construção'}
                                    getOptionLabel={(option) => option.nome}
                                    value={batchFasesNomes.map(fn => fases.find(f => f.nome === fn)).filter(Boolean)}
                                    onChange={(_, newValue) => setBatchFasesNomes(newValue.map((v: any) => v.nome))}
                                    renderInput={(params) => <TextField {...params} label="Fases para todos" />}
                                />
                                <Button size="small" variant="outlined" onClick={handleApplyBatchFases} disabled={batchFasesNomes.length === 0}>
                                    Aplicar
                                </Button>
                            </Box>
                        </Paper>

                        <TableContainer component={Paper} sx={{ maxHeight: '55vh' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Descrição *</TableCell>
                                        <TableCell>Subcategoria *</TableCell>
                                        <TableCell>Fase da Obra *</TableCell>
                                        <TableCell>Valor Padrão</TableCell>
                                        <TableCell>Ação</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item) => {
                                        const selectedCat = hierarchicalCategorias.find(c => c.nome === item.categoria_nome && !c.isParent) || null;
                                        const missingFase = !item.fases_nomes || item.fases_nomes.length === 0;
                                        return (
                                            <TableRow key={item.id_temp} sx={{ bgcolor: item.similarExists ? 'warning.light' : 'inherit' }}>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        value={item.descricao}
                                                        error={!item.descricao?.trim()}
                                                        onChange={(e) => handleItemChange(item.id_temp, 'descricao', e.target.value)}
                                                        fullWidth
                                                        sx={{ minWidth: 220 }}
                                                    />
                                                    {item.similarExists && (
                                                        <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.5, fontWeight: 'bold' }}>
                                                            ⚠️ Similar no catálogo: {item.similarItemName}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Autocomplete
                                                        size="small"
                                                        options={hierarchicalCategorias}
                                                        getOptionLabel={(option) => option.parentName ? `${option.parentName} > ${option.nome}` : option.nome}
                                                        value={selectedCat}
                                                        onChange={(event, newValue) => {
                                                            handleItemChange(item.id_temp, 'categoria_nome', newValue ? newValue.nome : '');
                                                        }}
                                                        renderOption={(props, option) => (
                                                            <li
                                                                {...props}
                                                                style={{
                                                                    paddingLeft: `${(option.level * 20) + 10}px`,
                                                                    fontWeight: option.level === 0 ? 700 : 400
                                                                }}
                                                            >
                                                                {option.level > 0 ? '— '.repeat(option.level) : ''}{option.nome}
                                                            </li>
                                                        )}
                                                        getOptionDisabled={(option) => option.isParent}
                                                        renderInput={(params) => <TextField {...params} error={!selectedCat} placeholder="Selecione" />}
                                                        fullWidth
                                                        sx={{ minWidth: 200 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Autocomplete
                                                        multiple
                                                        size="small"
                                                        options={fases}
                                                        groupBy={(option) => option.tipo_fluxo === 'leilao' ? '🏠 Leilão' : '🏗️ Construção'}
                                                        getOptionLabel={(option) => option.nome}
                                                        value={item.fases_nomes.map(fn => fases.find(f => f.nome === fn)).filter(Boolean) as any[]}
                                                        onChange={(event, newValue) => {
                                                            handleItemChange(item.id_temp, 'fases_nomes', newValue.map(v => v.nome));
                                                        }}
                                                        renderTags={(value, getTagProps) =>
                                                            value.map((option, index) => {
                                                                const { key, ...tagProps } = getTagProps({ index });
                                                                return <Chip key={key} size="small" variant="outlined" label={option.nome} {...tagProps} />;
                                                            })
                                                        }
                                                        renderInput={(params) => (
                                                            <TextField {...params} variant="outlined" placeholder="Fases" error={missingFase} />
                                                        )}
                                                        fullWidth
                                                        sx={{ minWidth: 240 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={item.valor_unitario_padrao ?? 0}
                                                        onChange={(e) => handleItemChange(item.id_temp, 'valor_unitario_padrao', parseFloat(e.target.value) || 0)}
                                                        sx={{ width: 100 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton color="error" size="small" onClick={() => handleRemoveItem(item.id_temp)}>
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {step === 3 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                        <Alert severity="success" sx={{ mb: 2 }}>{items.length} Itens processados com sucesso!</Alert>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {step === 1 && (
                    <>
                        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
                        <Button onClick={processDocument} variant="contained" disabled={!file || loading}>
                            {loading ? <CircularProgress size={24} /> : 'Processar Documento'}
                        </Button>
                    </>
                )}
                {step === 2 && (
                    <>
                        <Button onClick={() => setStep(1)} disabled={loading}>Voltar</Button>
                        <Button onClick={handleConfirm} variant="contained" disabled={loading || items.length === 0}>
                            {loading ? <CircularProgress size={24} /> : `Confirmar ${items.length} Itens`}
                        </Button>
                    </>
                )}
                {step === 3 && (
                    <Button onClick={() => { onClose(); onSuccess(); }} variant="contained">
                        Fechar
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ImportDocumentModal;
